import React from "react";
import { useTranslation } from "react-i18next";
import { useGetCartQuery } from "../../api/cartApi";
import Icon from "../../../../../common/components/Icon/Icon";

const FreeShippingProgressBar: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const { data: cart, isLoading } = useGetCartQuery();

  // --- 1. Early Exit Conditions (Render Nothing) ---

  // Don't render if data is loading, translations are not ready, or there's no cart data.
  if (isLoading || !areTranslationsReady || !cart) {
    return null;
  }

  const {
    isFreeShippingAvailable,
    freeShippingRequirement,
    hasFreeShippingCoupon,
    freeShippingThreshold,
    amountNeededForFreeShipping,
    formattedAmountNeededForFreeShipping,
  } = cart;

  // Case: Free shipping is not configured for the customer's zone.
  if (freeShippingRequirement === "not_configured") {
    return null;
  }

  // Case: Requires a coupon, but the user hasn't applied one yet.
  if (freeShippingRequirement === "coupon" && !hasFreeShippingCoupon) {
    return null;
  }

  // Case: Requires BOTH a coupon AND a minimum amount, but not both conditions are met.
  // The `isFreeShippingAvailable` flag already represents this combined state.
  if (freeShippingRequirement === "both" && !isFreeShippingAvailable) {
    return null;
  }

  // --- 2. Render Logic (Component will be displayed) ---

  const hasReachedThreshold = isFreeShippingAvailable;

  // Calculate progress percentage, ensuring it's between 0 and 100.
  // This is only relevant for 'min_amount' or 'either' requirements.
  let progressPercentage = 0;
  if (freeShippingThreshold && freeShippingThreshold > 0) {
    const subtotal = hasReachedThreshold
      ? freeShippingThreshold
      : freeShippingThreshold - (amountNeededForFreeShipping || 0);
    progressPercentage = Math.min(
      (subtotal / freeShippingThreshold) * 100,
      100
    );
  } else if (hasReachedThreshold) {
    progressPercentage = 100; // For 'none' or 'coupon' success states
  }

  const message = hasReachedThreshold
    ? t("freeShippingProgressBar.successMessage", "You've got free shipping!")
    : t("freeShippingProgressBar.message", {
        amount: formattedAmountNeededForFreeShipping,
        defaultValue: "Add {{amount}} more to your cart to get free shipping!",
      });

  const rootClassName = `free-shipping-progress-bar ${
    hasReachedThreshold ? "is-achieved" : ""
  }`;

  return (
    <div className={rootClassName}>
      <p className="free-shipping-progress-bar__message">
        <Icon
          iconName="package-outlined"
          className="free-shipping-progress-bar__icon"
        />
        {message}
      </p>
      <div className="free-shipping-progress-bar__progress-bar">
        <div
          className="free-shipping-progress-bar__progress"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>
    </div>
  );
};

export default FreeShippingProgressBar;
